import { useState } from 'react';
import { ContentType, UNKNOWN_ANCHOR_TITLE } from '@vivport/backend/types';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ItemSelector } from '@/components/widget/item-selector';
import { useContentList } from '@/hooks/useContentQueries';

export interface KeepUnderProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (anchorId: number) => void;
  pending: boolean;
  description: string;
}

export function KeepUnderProjectDialog({
  open,
  onClose,
  onConfirm,
  pending,
  description,
}: KeepUnderProjectDialogProps) {
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const anchorsQuery = useContentList({ contentTypes: [ContentType.ANCHOR], limit: 100 });
  const anchorItems = (anchorsQuery.data?.items ?? [])
    .map((entry: { id: number; title?: string | null }) => ({
      id: entry.id,
      title: typeof entry.title === 'string' ? entry.title : null,
    }))
    .filter((entry) => entry.title !== UNKNOWN_ANCHOR_TITLE);

  const close = () => {
    setAnchorId(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Keep under project</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody className="min-w-0 space-y-2 text-sm">
          <ItemSelector
            entityName="anchor"
            displayName="Project"
            items={anchorItems}
            selectedId={anchorId}
            onSelect={setAnchorId}
            loading={anchorsQuery.isLoading}
          />
        </DialogBody>
        <DialogFooter className="gap-2 sm:flex-col">
          <Button
            type="button"
            className="w-full"
            disabled={anchorId === null || pending}
            onClick={() => {
              if (anchorId !== null) onConfirm(anchorId);
            }}
          >
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Keep
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={close}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
